import { fetchBaaStatus } from "@app/agreements";
import {
  createSupportTicket,
  fetchActivePlans,
  fetchPlans,
  fetchStacks,
  getStackType,
  resetSupportTicket,
  selectFirstActivePlan,
  selectIsUserAnyOwner,
  selectPlanByActiveId,
  selectStacksByOrgAsList,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useDispatch, useSelector } from "@app/react";
import { useLoader, useQuery } from "@app/react";
import { plansUrl, settingsLegalAgreementsUrl, stacksUrl } from "@app/routes";
import { schema } from "@app/schema";
import type { AgreementSummary } from "@app/types";
import { selectCurrentUser } from "@app/users";
import { stackNameRegexExplainer, stackNameValidator } from "@app/validator";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Banner,
  BannerMessages,
  Box,
  Breadcrumbs,
  ButtonAnyOwner,
  CheckBox,
  ExternalLink,
  FormGroup,
  Group,
  Input,
  Select,
  type SelectOption,
  Tooltip,
  tokens,
} from "../shared";

const regionOptions: SelectOption[] = [
  { label: "Select a Region", value: "none" },

  { label: "us-east-1 / US East (N. Virginia)", value: "us-east-1" },
  { label: "us-east-2 / US East (Ohio)", value: "us-east-2" },
  { label: "us-west-1 / US West (N. California)", value: "us-west-1" },
  { label: "us-west-2 / US West (Oregon)", value: "us-west-2" },

  { label: "ca-central-1 / Canada (Central)", value: "ca-central-1" },

  { label: "eu-west-1 / Europe (Ireland)", value: "eu-west-1" },
  { label: "eu-west-2 / Europe (London)", value: "eu-west-2" },
  { label: "eu-west-3 / Europe (Paris)", value: "eu-west-3" },
  { label: "eu-central-1 / Europe (Frankfurt)", value: "eu-central-1" },

  { label: "ap-south-1 / Asia Pacific (Mumbai)", value: "ap-south-1" },
  {
    label: "ap-southeast-1 / Asia Pacific (Singapore)",
    value: "ap-southeast-1",
  },
  { label: "ap-southeast-2 / Asia Pacific (Sydney)", value: "ap-southeast-2" },
  { label: "ap-northeast-1 / Asia Pacific (Tokyo)", value: "ap-northeast-1" },
  { label: "ap-northeast-2 / Asia Pacific (Seoul)", value: "ap-northeast-2" },

  { label: "sa-east-1 / South America (São Paulo)", value: "sa-east-1" },
];

const hasDataTypeSelected = (dataTypes: string[]) => dataTypes.length > 0;

const DATA_TYPE_OPTIONS = [
  { value: "phi", label: "Protected Health Information (PHI)" },
  { value: "sensitive", label: "Sensitive Data" },
  { value: "financial", label: "Financial Data" },
  { value: "other", label: "Other" },
  { value: "none", label: "None" },
];

const RequiredLabel = ({ children }: { children: React.ReactNode }) => (
  <>
    {children} <span className="text-red-500">*</span>
  </>
);

export const CreateStackPage = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const orgId = useSelector(selectOrganizationSelectedId);
  const activePlan = useSelector(selectFirstActivePlan);
  const stacks = useSelector(selectStacksByOrgAsList);
  const isOwner = useSelector(selectIsUserAnyOwner);

  const hasDedicatedStack = useMemo(
    () =>
      stacks.some(
        (s) => s.organizationId === orgId && getStackType(s) === "dedicated",
      ),
    [stacks, orgId],
  );

  const baaFetchAction = useMemo(() => fetchBaaStatus({ orgId }), [orgId]);
  const baaLoader = useLoader(baaFetchAction);
  const baaSummary = useSelector((s) =>
    schema.cache.selectById(s, { id: baaFetchAction.payload.key }),
  ) as AgreementSummary | undefined;
  const baaStatus = baaSummary?.status;
  const baaStatusKnown = baaStatus !== undefined;

  useQuery(fetchPlans());
  useQuery(fetchActivePlans({ orgId }));
  useQuery(fetchStacks());

  const selectedPlan = useSelector((s) =>
    selectPlanByActiveId(s, { id: activePlan.planId }),
  );
  // must have a non-starter active plan or,
  // an empty active plan (empty active plan means legacy enterprise)
  const canRequestStack = [
    "growth",
    "scale",
    "production",
    "enterprise",
  ].includes(selectedPlan.name);

  const requiresSignedBaa = !hasDedicatedStack;
  const hasSignedBaa = baaStatus === "SIGNED";

  const [stackName, setStackName] = useState("");
  const [region, setRegion] = useState("none");
  const [dataTypes, setDataTypes] = useState<string[]>([]);
  const [terms, setTerms] = useState(false);
  const loader = useLoader(createSupportTicket);

  const phiSelected = dataTypes.includes("phi");
  const baaRelevant = canRequestStack && requiresSignedBaa;
  const showBaaBanner =
    baaRelevant &&
    !baaLoader.isLoading &&
    baaStatusKnown &&
    !hasSignedBaa &&
    phiSelected;
  const showBaaFetchError = baaRelevant && baaLoader.isError && !baaStatusKnown;

  const stackNameError = stackName ? stackNameValidator(stackName) : undefined;
  const requiredFieldsComplete =
    stackNameValidator(stackName) === undefined &&
    region !== "none" &&
    hasDataTypeSelected(dataTypes);
  const submitBlockers: [boolean, string][] = [
    [
      !canRequestStack,
      "Production or Enterprise plan required to request a Dedicated Stack.",
    ],
    [!requiredFieldsComplete, "Complete required fields to continue."],
    [
      showBaaBanner,
      "Sign the Business Associate Agreement before requesting a Dedicated Stack to process PHI.",
    ],
    [
      !terms,
      "You must agree to the terms and conditions to submit this request.",
    ],
  ];
  const blocker = submitBlockers.find(([blocked]) => blocked);
  const submitDisabledReason = blocker ? blocker[1] : undefined;
  const isSubmitDisabled = submitDisabledReason !== undefined;

  const updateDataTypes =
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.currentTarget.checked) {
        setDataTypes(dataTypes.filter((dt) => dt !== name));
        return;
      }
      if (name === "none") {
        setDataTypes(["none"]);
        return;
      }
      setDataTypes([...dataTypes.filter((dt) => dt !== "none"), name]);
    };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitDisabled) {
      return;
    }

    dispatch(
      createSupportTicket({
        email: user.email,
        name: user.name,
        subject: `Dedicated Stack Request - ${stackName} on ${region}`,
        priority: "high",
        attachments: [],
        description: [
          `ORG_ID="${orgId}";`,
          `STACK_NAME="${stackName}";`,
          `REGION="${region}";`,
          `Data Types: ${dataTypes.join(", ")}`,
          `Current plan: ${selectedPlan.name}`,
        ].join("\n"),
      }),
    );
  };

  useEffect(() => {
    return () => {
      // reset loader when component is unmounted because we have multiple
      // pages that use this same loader id
      dispatch(resetSupportTicket());
    };
  }, []);

  useEffect(() => {
    if (!orgId) return;
    dispatch(baaFetchAction);
  }, [orgId, dispatch, baaFetchAction]);

  return (
    <Group>
      <Breadcrumbs
        crumbs={[
          { name: "Stacks", to: stacksUrl() },
          { name: "Request Dedicated Stack", to: null },
        ]}
      />
      <Box>
        <form onSubmit={onSubmit}>
          <Group>
            <p>
              <ExternalLink
                variant="info"
                href="https://www.aptible.com/docs/stacks#dedicated-stacks"
              >
                Dedicated Stacks
              </ExternalLink>{" "}
              are built for production environments, are dedicated to a single
              organization, and provide four significant benefits: Isolated
              Tenancy, SLA Availability, Regulatory (HIPAA, GDPR), and
              Connectivity (VPNs, VPC Peering).
            </p>

            {canRequestStack ? (
              <Banner variant="info">
                Fill out this form and Aptible Support will be in contact with
                you shortly.
              </Banner>
            ) : null}

            {selectedPlan.id === "" || canRequestStack ? null : (
              <Banner variant="info">
                Production or Enterprise plan required for access to{" "}
                <strong>Dedicated Stacks and BAAs</strong> &mdash;{" "}
                <Link to={plansUrl()}>Upgrade your plan for access.</Link>
              </Banner>
            )}

            <BannerMessages {...loader} />

            <FormGroup
              label={<RequiredLabel>Stack Name</RequiredLabel>}
              description={stackNameRegexExplainer}
              feedbackMessage={stackNameError}
              feedbackVariant={stackNameError ? "danger" : "info"}
              htmlFor="stack"
            >
              <Input
                id="stack"
                name="stack"
                value={stackName}
                onChange={(e) => setStackName(e.currentTarget.value)}
              />
            </FormGroup>

            <FormGroup
              label={<RequiredLabel>Region</RequiredLabel>}
              description="Stack will be created in this AWS Region"
              htmlFor="region"
            >
              <Select
                options={regionOptions}
                value={region}
                onSelect={(opt) => setRegion(opt.value)}
              />
            </FormGroup>

            <FormGroup
              label={
                <RequiredLabel>
                  What types of data will this Stack process?
                </RequiredLabel>
              }
              description="Select all that apply"
              htmlFor="data-type"
            >
              <Group size="sm">
                {DATA_TYPE_OPTIONS.map((opt) => (
                  <CheckBox
                    key={opt.value}
                    label={opt.label}
                    checked={dataTypes.includes(opt.value)}
                    onChange={updateDataTypes(opt.value)}
                  />
                ))}
              </Group>

              {showBaaBanner ? (
                <Banner
                  variant="warning"
                  className="w-full border-2 border-gold bg-gold-50 shadow-sm mt-3"
                >
                  You must sign a Business Associate Agreement (BAA) before
                  requesting a Dedicated Stack to process PHI. Complete the
                  agreement on the{" "}
                  <Link
                    className="font-semibold underline"
                    to={settingsLegalAgreementsUrl()}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Legal &amp; Compliance
                  </Link>{" "}
                  page, then refresh this page to submit your request.
                </Banner>
              ) : null}
            </FormGroup>

            <div>
              <h4 className={tokens.type.h4}>Monthly Cost</h4>
              <p className="text-lg text-green-400">$499</p>
            </div>

            <hr />

            <Group
              variant="horizontal"
              size="sm"
              className="items-center gap-4"
            >
              {isOwner && isSubmitDisabled ? (
                <Tooltip text={submitDisabledReason} fluid>
                  <span>
                    <ButtonAnyOwner
                      type="submit"
                      disabled
                      isLoading={loader.isLoading}
                    >
                      Request Dedicated Stack
                    </ButtonAnyOwner>
                  </span>
                </Tooltip>
              ) : (
                <ButtonAnyOwner
                  type="submit"
                  disabled={isSubmitDisabled}
                  isLoading={loader.isLoading}
                >
                  Request Dedicated Stack
                </ButtonAnyOwner>
              )}
              <CheckBox
                onChange={(e) => setTerms(e.currentTarget.checked)}
                label={
                  <>
                    <span>I agree to the </span>
                    <ExternalLink
                      variant="info"
                      href="https://www.aptible.com/legal/terms-of-service"
                    >
                      terms and conditions
                    </ExternalLink>
                  </>
                }
              />
            </Group>

            {baaRelevant && baaLoader.isLoading ? (
              <p className="text-gray-600 text-sm w-full">
                Verifying Business Associate Agreement status…
              </p>
            ) : null}
            {showBaaFetchError ? (
              <p className="text-red-500 text-sm w-full">
                Could not verify your BAA status. Refresh the page and try
                again.
              </p>
            ) : null}
          </Group>
        </form>
      </Box>
    </Group>
  );
};
